
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { InventoryItem } from "@/types/inventory";

const EditInventoryItem = () => {
  const { itemId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    quantity: 1,
    has_expiry: true,
    expiry_date: null as Date | null,
    location: "",
    barcode: ""
  });
  const [date, setDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    const fetchItemDetails = async () => {
      if (!user || !itemId) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('inventory_items')
          .select('*')
          .eq('id', itemId)
          .single();
          
        if (error) throw error;
        
        setItem(data);
        setFormData({
          name: data.name || "",
          description: data.description || "",
          category: data.category || "",
          quantity: data.quantity || 1,
          has_expiry: data.has_expiry,
          expiry_date: data.expiry_date ? new Date(data.expiry_date) : null,
          location: data.location || "",
          barcode: data.barcode || ""
        });
        
        if (data.expiry_date) {
          setDate(new Date(data.expiry_date));
        }
      } catch (error: any) {
        console.error("Error fetching item details:", error.message);
        toast.error("Failed to load item details");
        navigate("/inventory");
      } finally {
        setLoading(false);
      }
    };
    
    fetchItemDetails();
  }, [user, itemId, navigate]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseInt(value) || 1 }));
  };
  
  const handleHasExpiryChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, has_expiry: checked }));
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    setDate(date);
    setFormData(prev => ({ ...prev, expiry_date: date || null }));
  };
  
  const handleSave = async () => {
    if (!user || !itemId) return;
    
    try {
      setSaving(true);
      
      const { error } = await supabase
        .from('inventory_items')
        .update({
          name: formData.name,
          description: formData.description || null,
          category: formData.category,
          quantity: formData.quantity,
          has_expiry: formData.has_expiry,
          expiry_date: formData.expiry_date ? format(formData.expiry_date, 'yyyy-MM-dd') : null,
          location: formData.location || null,
          barcode: formData.barcode || null
        })
        .eq('id', itemId);
        
      if (error) throw error;
      
      toast.success("Item updated successfully", {
        position: "bottom-right",
        duration: 3000,
      });
      
      navigate("/inventory");
    } catch (error: any) {
      console.error("Error updating item:", error.message);
      toast.error("Failed to update item");
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate("/inventory");
  };
  
  if (loading) {
    return (
      <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-600">Loading item details...</span>
          </div>
        </div>
      </PageTransition>
    );
  }
  
  return (
    <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Edit Inventory Item</h1>
          
          <Card>
            <CardHeader>
              <CardTitle>Item Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Item name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Item description"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="Category"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity</Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={handleNumberChange}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="has_expiry" 
                    checked={formData.has_expiry}
                    onCheckedChange={handleHasExpiryChange}
                  />
                  <Label htmlFor="has_expiry">This item has an expiration date</Label>
                </div>
              </div>
              
              {formData.has_expiry && (
                <div className="space-y-2">
                  <Label>Expiration Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Select expiration date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={handleDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="location">Storage Location (Optional)</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Where is this item stored?"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="barcode">Barcode (Optional)</Label>
                <Input
                  id="barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleInputChange}
                  placeholder="Item barcode"
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
};

export default EditInventoryItem;
